import {
  useCallback,
  useRef,
  type CSSProperties,
  type MutableRefObject,
  type ReactNode,
  type RefObject
} from 'react';
import { cn } from 'v5/platform/dom/cn';
import { useGridKeyboardNavigation } from 'v5/platform/react/grid-keyboard-nav/useGridKeyboardNavigation';
import { focusRings } from 'v5/design-system/_common/focusRing';
import {
  type ColorPickerLibraryItem,
  type IconPickerLibraryItem
} from './ColorPicker.library';
import { isLightColor, normalizeHex } from './ColorPicker.utils';

export const COLOR_PICKER_GRID_COLUMNS = 8;

const colorCellClassName = cn(
  'relative flex size-8 cursor-pointer items-center justify-center rounded-full p-0 outline-none transition-shadow',
  focusRings.focus.normal,
  'ring-1 ring-border-default',
  'data-selected:ring-2 data-selected:ring-fg-default',
  'z-0 focus:z-10'
);

const iconCellClassName = cn(
  'relative flex size-9 cursor-pointer items-center justify-center rounded-md p-0 outline-none transition-colors',
  focusRings.focus.normal,
  'text-fg-muted hover:bg-bg-subtle hover:text-fg-default',
  'data-selected:bg-bg-subtle data-selected:text-fg-default',
  'z-0 focus:z-10'
);

type PickerGridCell = {
  id: string;
  label: string;
  selected?: boolean;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

function chunkRows<T>(items: readonly T[], columns: number): T[][] {
  if (columns <= 0 || items.length === 0) {
    return items.length === 0 ? [] : [Array.from(items)];
  }
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += columns) {
    rows.push(Array.from(items.slice(i, i + columns)));
  }
  return rows;
}

function PickerGrid({
  ariaLabel,
  columns,
  rows,
  onActivate,
  className,
  gridRef
}: {
  ariaLabel: string;
  columns: number;
  rows: readonly (readonly PickerGridCell[])[];
  onActivate: (id: string) => void;
  className?: string;
  gridRef?: RefObject<HTMLDivElement>;
}) {
  const localRef = useRef<HTMLDivElement | null>(null);
  const setGridRef = useCallback(
    (node: HTMLDivElement | null) => {
      localRef.current = node;
      if (gridRef) {
        (gridRef as MutableRefObject<HTMLDivElement | null>).current = node;
      }
    },
    [gridRef]
  );

  useGridKeyboardNavigation(localRef);

  return (
    <div
      ref={setGridRef}
      role="grid"
      aria-label={ariaLabel}
      aria-colcount={columns}
      className={cn('flex flex-col gap-2 outline-none', className)}
    >
      {rows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          role="row"
          className="grid gap-2"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {row.map(cell => (
            <div key={cell.id} role="gridcell" className="min-w-0">
              <button
                type="button"
                data-selected={cell.selected ? '' : undefined}
                aria-label={cell.label}
                aria-pressed={cell.selected}
                className={cn(cell.className)}
                style={cell.style}
                onClick={() => onActivate(cell.id)}
              >
                {cell.children}
              </button>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export function focusPickerGridList(root: HTMLElement | null) {
  if (!root) {
    return;
  }
  const focusTarget =
    root.querySelector<HTMLElement>('[data-selected]') ??
    root.querySelector<HTMLElement>('[role="gridcell"] button') ??
    root.querySelector<HTMLElement>('button');
  focusTarget?.focus();
}

export function usePickerGridFocusRef() {
  const ref = useRef<HTMLDivElement | null>(null);
  const focusGrid = useCallback(() => {
    requestAnimationFrame(() => focusPickerGridList(ref.current));
  }, []);
  return { ref, focusGrid };
}

export type ColorPickerGridGalleryProps = {
  ariaLabel: string;
  colors: readonly ColorPickerLibraryItem[];
  selectedId: string;
  onSelectedIdChange: (id: string) => void;
  /** Called when the user activates the already-selected swatch (e.g. Enter). */
  onClose?: () => void;
  columns?: number;
  className?: string;
  gridRef?: RefObject<HTMLDivElement>;
};

export function ColorPickerGridGallery({
  ariaLabel,
  colors,
  selectedId,
  onSelectedIdChange,
  onClose,
  columns = COLOR_PICKER_GRID_COLUMNS,
  className,
  gridRef
}: ColorPickerGridGalleryProps) {
  const cells: PickerGridCell[] = colors.map(item => {
    const hex = normalizeHex(item.value) ?? item.value;
    return {
      id: item.id,
      label: item.name,
      selected: item.id === selectedId,
      className: colorCellClassName,
      children: (
        <span className="flex size-full items-center justify-center overflow-hidden rounded-full">
          {item.render ?? (
            <span
              className="size-full rounded-full"
              style={{ backgroundColor: hex }}
            />
          )}
        </span>
      )
    };
  });

  return (
    <PickerGrid
      ariaLabel={ariaLabel}
      columns={columns}
      gridRef={gridRef}
      className={cn(focusRings.focusWithin.visible, className)}
      rows={chunkRows(cells, columns)}
      onActivate={id => {
        if (id === selectedId) {
          onClose?.();
          return;
        }
        onSelectedIdChange(id);
      }}
    />
  );
}

export type IconPickerGridGalleryProps = {
  ariaLabel: string;
  icons: readonly IconPickerLibraryItem[];
  selectedId: string;
  onSelectedIdChange: (id: string) => void;
  /** Called when the user activates the already-selected icon (e.g. Enter). */
  onClose?: () => void;
  colorHex?: string;
  columns?: number;
  className?: string;
  gridRef?: RefObject<HTMLDivElement>;
};

export function IconPickerGridGallery({
  ariaLabel,
  icons,
  selectedId,
  onSelectedIdChange,
  onClose,
  colorHex,
  columns = COLOR_PICKER_GRID_COLUMNS,
  className,
  gridRef
}: IconPickerGridGalleryProps) {
  const onDark = colorHex ? !isLightColor(colorHex) : false;

  const cells: PickerGridCell[] = icons.map(item => {
    const selected = item.id === selectedId;
    return {
      id: item.id,
      label: item.name,
      selected,
      className: iconCellClassName,
      style: selected && colorHex ? { backgroundColor: colorHex } : undefined,
      children: (
        <span
          className={cn(
            'flex items-center justify-center [&_svg]:size-4',
            selected && colorHex && (onDark ? 'text-white' : 'text-black')
          )}
        >
          {item.render}
        </span>
      )
    };
  });

  return (
    <PickerGrid
      ariaLabel={ariaLabel}
      columns={columns}
      gridRef={gridRef}
      className={className}
      rows={chunkRows(cells, columns)}
      onActivate={id => {
        if (id === selectedId) {
          onClose?.();
          return;
        }
        onSelectedIdChange(id);
      }}
    />
  );
}

const colorKey = (id: string) => `color:${id}`;
const iconKey = (id: string) => `icon:${id}`;

export type ColorIconPickerGridGalleryProps = {
  ariaLabel: string;
  colors: readonly ColorPickerLibraryItem[];
  icons: readonly IconPickerLibraryItem[];
  selectedColorId: string;
  selectedIconId: string;
  onSelectedColorIdChange: (id: string) => void;
  onSelectedIconIdChange: (id: string) => void;
  /** Called when the user activates an already-selected color or icon (e.g. Enter). */
  onClose?: () => void;
  colorHex?: string;
  columns?: number;
  className?: string;
  gridRef?: RefObject<HTMLDivElement>;
};

/**
 * One keyboard grid: color rows then icon rows. Arrow keys move across both
 * via the shared {@link useGridKeyboardNavigation} (same as resourcing tables).
 */
export function ColorIconPickerGridGallery({
  ariaLabel,
  colors,
  icons,
  selectedColorId,
  selectedIconId,
  onSelectedColorIdChange,
  onSelectedIconIdChange,
  onClose,
  colorHex,
  columns = COLOR_PICKER_GRID_COLUMNS,
  className,
  gridRef
}: ColorIconPickerGridGalleryProps) {
  const onDark = colorHex ? !isLightColor(colorHex) : false;

  const selectedColorItemKey = selectedColorId
    ? colorKey(selectedColorId)
    : null;
  const selectedIconItemKey = selectedIconId ? iconKey(selectedIconId) : null;

  const colorCells: PickerGridCell[] = colors.map(item => {
    const hex = normalizeHex(item.value) ?? item.value;
    const id = colorKey(item.id);
    return {
      id,
      label: item.name,
      selected: id === selectedColorItemKey,
      className: cn(colorCellClassName, 'mb-1'),
      children: (
        <span className="flex size-full items-center justify-center overflow-hidden rounded-full">
          {item.render ?? (
            <span
              className="size-full rounded-full"
              style={{ backgroundColor: hex }}
            />
          )}
        </span>
      )
    };
  });

  const iconCells: PickerGridCell[] = icons.map(item => {
    const id = iconKey(item.id);
    const selected = item.id === selectedIconId;
    return {
      id,
      label: item.name,
      selected,
      className: iconCellClassName,
      style: selected && colorHex ? { backgroundColor: colorHex } : undefined,
      children: (
        <span
          className={cn(
            'flex items-center justify-center [&_svg]:size-4',
            selected && colorHex && (onDark ? 'text-white' : 'text-black')
          )}
        >
          {item.render}
        </span>
      )
    };
  });

  return (
    <PickerGrid
      ariaLabel={ariaLabel}
      columns={columns}
      gridRef={gridRef}
      className={cn('gap-y-2', className)}
      rows={[
        ...chunkRows(colorCells, columns),
        ...chunkRows(iconCells, columns)
      ]}
      onActivate={id => {
        if (id === selectedColorItemKey || id === selectedIconItemKey) {
          onClose?.();
          return;
        }
        if (id.startsWith('color:')) {
          onSelectedColorIdChange(id.slice('color:'.length));
          return;
        }
        if (id.startsWith('icon:')) {
          onSelectedIconIdChange(id.slice('icon:'.length));
        }
      }}
    />
  );
}
